/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable @typescript-eslint/member-ordering */
import { isEqual, merge, cloneDeep } from 'lodash-es';
import { useRouteMatch } from './match/use-route-match';

type Fetcher<TData, TParams extends any[]> = (
  ...args: TParams
) => Promise<TData>;

interface Options<TParams extends any[]> {
  defaultParams?: TParams;
  getDefaultParams?: () => TParams;
  /** cache expiration time @default 60s */
  cacheTime?: number;

  // for report
  name?: string;
}

const defaultOptions: Options<any[]> = {
  cacheTime: 1000 * 60,
};

class Prefetch<TData, TParams extends any[]> {
  private readonly originFetcher: Fetcher<TData, TParams>;

  private readonly wrappedFetcher: Fetcher<TData, TParams>;

  private readonly option: Options<TParams>;

  // cached prefetch promise
  private prefetchPromise: Promise<TData> | undefined;

  private prefetchParams: TParams | undefined;

  private setTimeoutKey: any;

  public get fetcher() {
    return this.wrappedFetcher;
  }

  constructor(fetcher: Fetcher<TData, TParams>, option?: Options<TParams>) {
    this.originFetcher = fetcher;
    this.wrappedFetcher = this.genWrappedFetcher();
    this.option = merge(cloneDeep(defaultOptions), option);
  }

  private genWrappedFetcher() {
    return async (...p: TParams) => {
      const currentParams = p.length ? p : undefined;
      // Check if the cache is available
      if (this.prefetchPromise) {
        if (isEqual(this.prefetchParams, currentParams)) {
          try {
            const data = await this.getPrefetchPromiseAndClear();
            if (data) {
              return data;
            }
          } catch (e) {}
        }
      }
      return this.originFetcher(...p);
    };
  }

  /**
   * Add to the cache and set the expiration time
   * @private
   */
  private save(params: TParams | undefined, res: Promise<TData>) {
    this.prefetchParams = params;
    this.prefetchPromise = res.then(
      d => d,
      error => {
        this.clear();
        // eslint-disable-next-line promise/no-return-wrap
        return Promise.reject(error);
      },
    );

    window.clearTimeout(this.setTimeoutKey);
    this.setTimeoutKey = window.setTimeout(() => {
      this.clear();
    }, this.option.cacheTime);
  }

  public clear() {
    this.prefetchPromise = undefined;
    this.prefetchParams = undefined;
    window.clearTimeout(this.setTimeoutKey);
  }

  private getDefaultParamsFromOption(): TParams {
    // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
    return this.option.defaultParams ?? this.option.getDefaultParams?.()!;
  }

  /**
   * send prefetch
   * @param p
   */
  public run(...p: TParams) {
    const params: TParams | undefined = p.length
      ? p
      : this.getDefaultParamsFromOption();
    const res = this.originFetcher(...(params ?? []));
    this.save(params, res);
    return res;
  }

  public getPrefetchPromise() {
    return this.prefetchPromise;
  }

  public getPrefetchPromiseAndClear() {
    const p = this.prefetchPromise;
    this.clear();
    return p;
  }
}

export { Prefetch, useRouteMatch };
